import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { requestOtp, verifyOtp, type DeliveryMethod, type OtpPurpose } from '@/services/auth';
import { useOtpInput } from '@/hooks/use-otp-input';
import { otpSchema, type OtpFormValues } from '@/lib/schemas';
import { track, AnalyticsEvent } from '@/lib/analytics/tracker';
import { getErrorCode } from '@/lib/error-utils';
import { otpBlockOf, type OtpBlockReason } from '@/helpers/auth';
import { GENERIC_ERROR_MESSAGE } from '@/lib/constants';
import { formatCountdown } from '@/lib/utils';

interface UseOtpFlowOptions {
  purpose: OtpPurpose;
  onVerifySuccess: (response: any) => void;
  invalidEmailMessage?: string;
}

export function useOtpFlow({ purpose, onVerifySuccess, invalidEmailMessage }: UseOtpFlowOptions) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [formError, setFormError] = useState('');
  const [email, setEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('email');
  const [hasPhone, setHasPhone] = useState(false);
  const [isSwitchingMethod, setIsSwitchingMethod] = useState(false);
  const [block, setBlock] = useState<{
    reason: OtpBlockReason;
    until: number;
  } | null>(null);
  const [blockCountdown, setBlockCountdown] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [otpStepEntryCount, setOtpStepEntryCount] = useState(0);

  const isProcessingRef = useRef(false);
  isProcessingRef.current = isProcessing;

  const otp = useOtpInput();
  const otpForm: UseFormReturn<OtpFormValues> = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
  });

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (!block) return;
    const tick = () => {
      const remaining = block.until - Date.now();
      if (remaining <= 0) {
        setBlock(null);
        setBlockCountdown('');
        return;
      }
      setBlockCountdown(formatCountdown(remaining));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [block]);

  useEffect(() => {
    if (otpStepEntryCount > 0) {
      const timer = setTimeout(() => otp.focusFirst(), 300);
      return () => clearTimeout(timer);
    }
  }, [otpStepEntryCount, otp.focusFirst]);

  const sendOtp = async (emailAddr: string, method: DeliveryMethod = 'email') => {
    const response = await requestOtp({
      email: emailAddr,
      delivery_method: method,
      purpose,
    });
    if (response) {
      setHasPhone(response.has_phone);
      if (response.delivery_method) {
        setDeliveryMethod(response.delivery_method);
      }
      setMaskedPhone(response.masked_phone ?? '');
    }
    return response;
  };

  const submitEmail = async (emailValue: string, initialMethod: DeliveryMethod = 'email') => {
    try {
      setIsProcessing(true);
      setFormError('');
      const normalized = emailValue.toLowerCase();
      await sendOtp(normalized, initialMethod);
      setEmail(normalized);
      setResendCooldown(60);
      setOtpStepEntryCount((c) => c + 1);
    } catch (error: any) {
      const blocked = otpBlockOf(error);
      if (blocked) {
        setBlock({
          reason: blocked.reason,
          until: Date.now() + blocked.retryAfterMs,
        });
        track({
          event: AnalyticsEvent.OTP_RATE_LIMITED,
          properties: { purpose, limit_type: blocked.reason },
        });
      } else if (getErrorCode(error) === 'INVALID_EMAIL') {
        setFormError(
          invalidEmailMessage ??
            "We couldn't find an account with that email. Check the spelling or create a new account."
        );
      } else {
        setFormError(error?.message || GENERIC_ERROR_MESSAGE);
      }
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const submitOtp = useCallback(
    async (data: OtpFormValues) => {
      if (!email) return;
      try {
        setIsProcessing(true);
        setFormError('');
        const response = await verifyOtp({
          email,
          otp: data.otp,
          remember_me: true,
        });
        onVerifySuccess(response);
      } catch (error: any) {
        setFormError(error?.message || 'Invalid code. Please try again.');
        track({
          event: AnalyticsEvent.OTP_VERIFICATION_FAILED,
          properties: {
            purpose,
            failure_reason: error?.message || 'Invalid code',
          },
        });
        otp.reset();
        otp.focusFirst();
      } finally {
        setIsProcessing(false);
      }
    },
    [email, onVerifySuccess, otp.reset, otp.focusFirst]
  );

  const submitOtpRef = useRef(submitOtp);
  submitOtpRef.current = submitOtp;

  useEffect(() => {
    otpForm.setValue('otp', otp.combined, {
      shouldValidate: otp.combined.length === 6,
    });
    if (otp.combined.length === 6 && !isProcessingRef.current && email) {
      submitOtpRef.current({ otp: otp.combined });
    }
  }, [otp.combined, email]);

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    try {
      setFormError('');
      await sendOtp(email, deliveryMethod);
      setResendCooldown(60);
      otp.reset();
      otp.focusFirst();
      track({
        event: AnalyticsEvent.OTP_RESENT,
        properties: { purpose, delivery_method: deliveryMethod },
      });
    } catch (error: any) {
      setFormError(
        error?.message || 'Failed to resend code. Please try again.'
      );
    }
  };

  const handleSwitchDeliveryMethod = async () => {
    const fromMethod = deliveryMethod;
    const newMethod: DeliveryMethod =
      deliveryMethod === 'email' ? 'sms' : 'email';
    try {
      setIsSwitchingMethod(true);
      setFormError('');
      await sendOtp(email, newMethod);
      otp.reset();
      setResendCooldown(60);
      otp.focusFirst();
      track({
        event: AnalyticsEvent.OTP_DELIVERY_METHOD_SWITCHED,
        properties: {
          purpose,
          from_method: fromMethod,
          to_method: newMethod,
        },
      });
    } catch (error: any) {
      setFormError(
        error?.message || 'Failed to send code. Please try again.'
      );
    } finally {
      setIsSwitchingMethod(false);
    }
  };

  const resetOtpState = () => {
    otpForm.reset();
    otp.reset();
    setFormError('');
    setEmail('');
    setDeliveryMethod('email');
    setHasPhone(false);
    setMaskedPhone('');
    setBlock(null);
    setBlockCountdown('');
    setIsProcessing(false);
    setIsSwitchingMethod(false);
    setResendCooldown(0);
  };

  const clearBlock = () => {
    setBlock(null);
    setBlockCountdown('');
  };

  const canSubmitOtp = otp.combined.length === 6 && !isProcessing;

  return {
    isProcessing,
    formError,
    email,
    resendCooldown,
    deliveryMethod,
    hasPhone,
    isSwitchingMethod,
    block,
    blockCountdown,
    maskedPhone,
    otp,
    otpForm,
    canSubmitOtp,
    submitEmail,
    submitOtp,
    handleResendOtp,
    handleSwitchDeliveryMethod,
    resetOtpState,
    clearBlock,
  };
}
